import { SERVER_TIMEZONE } from '@shared/config/index';
import { LOGGER_SPACIAL_SYMBOL } from '@shared/utils/error/config';
import logger from '@shared/utils/error/logger';
import { formatMinutes, formatMinutesNew } from '@shared/utils/time/minutes';
import dayjs from 'dayjs';

class DT {
	instance: dayjs.Dayjs;
	constructor(value?: string | number) {
		if (!value) {
			this.instance = dayjs();
			return;
		}
		if (typeof value === 'number') {
			const finalValue = +value.toString().padEnd(13, '0');
			this.instance = dayjs(finalValue);
			return;
		}
		try {
			this.instance = dayjs.tz(value, SERVER_TIMEZONE);
		} catch (e) {
			logger.error(LOGGER_SPACIAL_SYMBOL.UTIL, 'dayjs init error', e);
			this.instance = dayjs();
		}
	}

	/**
	 * 从时间字符串 ｜ 时间戳创建 DT 实例
	 * @param value 时间字符串 ｜ 时间戳
	 * @returns
	 */
	static from(value: string | number | DT) {
		if (value instanceof DT) {
			return value;
		}
		return new DT(value);
	}

	static now() {
		return dayjs().valueOf();
	}

	/**
	 * 获取当前日期时间
	 * @returns {string} 日期时间字符串
	 */
	static getDataString(template?: string): string {
		return dayjs().format(template || 'YYYY-MM-DD');
	}

	/**
	 * 获取当前属于周一到周日中的哪一天
	 */
	static getDayOfWeek() {
		return dayjs().day();
	}

	/**
	 * 获取时间戳
	 * @returns {number} 时间戳
	 */
	valueOf(): number {
		return this.instance.valueOf();
	}

	/**
	 * 获取时间戳
	 * @returns {number} 时间戳
	 */
	timestamp(): number {
		return this.valueOf();
	}

	/**
	 * 减掉部分时间，单位 s;
	 */
	subtract(value: number) {
		return this.instance.subtract(value, 'second');
	}

	/**
	 * 增加部分时间，单位 s;
	 */
	add(value: number) {
		return this.instance.add(value, 'second');
	}

	/**
	 * 计算时间差，单位 s
	 *
	 * 注意：调用者减参数，并保留符号
	 *
	 * 示例：DT.from(expireTime).delta() 即：过期时间 - 当前时间
	 * @param refTime 时间（减数），不传则使用当前时间
	 * @returns
	 */
	delta(refTime?: string | DT) {
		const targetTime = refTime ? DT.from(refTime) : new DT();
		return (this.instance.valueOf() - targetTime.valueOf()) / 1000;
	}

	/**
	 * 计算已经过去的天数
	 *
	 * 示例：DT.from('2023-01-01').daysPassed() 返回从2023-01-01到当前的天数
	 * @returns {number} 过去的天数（向下取整）
	 */
	daysPassed(): number {
		const now = new DT();
		const diffMs = now.valueOf() - this.valueOf();
		return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
	}

	/**
	 * 展示创建、发布等时间
	 *
	 * 旧写法：formatMinutes(calcInterTime(pictureInfo.publishDate) / 60000)
	 *
	 * 新写法：DT.from(pictureInfo.publishDate).show()
	 * @returns {string} 用来展示的相对/绝对时间字符串
	 */
	show(): string {
		const dalta = new DT().delta(this);
		return formatMinutes(dalta / 60);
	}

	/**
	 * 展示消息时间	
	 * DT.from(pictureInfo.publishDate).showTime()
	 * @returns
	 */
	showTime(): string {
		const dalta = new DT().delta(this);
		return formatMinutesNew(dalta / 60);
	}

	/**
	 * 使用系统时区输出格式化字符串
	 * @param template {string} 格式模版，如：MM/DD/YYYY HH:mm:ss
	 * @returns
	 */
	format(template?: string): string {
		return this.instance.tz().format(template);
	}
}

export default DT;
